import React, { useState, useEffect, useRef } from "react";
import { ReloadOutlined, ExportOutlined } from "@ant-design/icons";
import { DEVICE_LOCAL_PAGE_URL } from "../../constants/constants";
import { useTranslation } from "react-i18next";
import MainBtn from "../main/main_btn/MainBtn";
import "./DeviceConfigurationEmbed.scss";

interface DeviceConfigurationEmbedProps {
  onPopupOpen?: (popup: Window) => void;
  onPopupClose?: () => void;
  onPopupStateChange?: (isOpen: boolean) => void;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

const DeviceConfigurationEmbed: React.FC<DeviceConfigurationEmbedProps> = ({
  onPopupOpen,
  onPopupClose,
  onPopupStateChange,
  fallbackTitle,
  fallbackDescription,
}) => {
  const { t } = useTranslation(["home", "wizard"]);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const [objectKey, setObjectKey] = useState(0);
  const [objectLoaded, setObjectLoaded] = useState(false);
  const objectRef = useRef<HTMLObjectElement>(null);

  const openDeviceConfigPopup = () => {
    const modalElement = document.querySelector('.soliseco-modal .modal-center');
    let left = window.screenX + (window.outerWidth / 2) - 450;
    let top = window.screenY + (window.outerHeight / 2) - 350;
    
    if (modalElement) {
      const rect = modalElement.getBoundingClientRect();
      if (rect.right + 920 < window.innerWidth) {
        left = window.screenX + rect.right + 20;
        top = window.screenY + rect.top;
      } else {
        left = window.screenX + (window.outerWidth / 2) - 450;
        top = window.screenY + (window.outerHeight / 2) - 350;
      }
    }

    const width = 900;
    const height = 700;

    const popup = window.open(
      DEVICE_LOCAL_PAGE_URL,
      'deviceConfig',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,location=no,menubar=no`
    );

    if (popup) {
      setPopupWindow(popup);
      onPopupOpen?.(popup);
      
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setPopupWindow(null);
          onPopupClose?.();
          onPopupStateChange?.(false);
        }
      }, 500);
      
      onPopupStateChange?.(true);

      popup.focus();
    } else {
      window.open(DEVICE_LOCAL_PAGE_URL, '_blank');
    }
  };

  useEffect(() => {
    return () => {
      if (popupWindow && !popupWindow.closed) {
        setPopupWindow(null);
        onPopupStateChange?.(false);
      }
    };
  }, [popupWindow, onPopupStateChange]);
  
  useEffect(() => {
    if (objectRef.current) {
      const checkObjectLoaded = () => {
        try {
          const obj = objectRef.current;
          if (obj) {
            const objDoc = obj.contentDocument || (obj as any).contentWindow?.document;
            if (objDoc && objDoc.body && objDoc.body.innerHTML.trim().length > 0) {
              // Check if it's not just the fallback content
              const bodyText = objDoc.body.textContent || '';
              const isErrorPage = bodyText.includes('ERR_') || 
                                bodyText.includes('blocked') ||
                                bodyText.includes('refused') ||
                                bodyText.includes('X-Frame-Options') ||
                                bodyText.includes('cannot be embedded');
              
              if (!isErrorPage) {
                setObjectLoaded(true);
                return true;
              }
            }
          }
        } catch (e) {
          // Cross-origin or security restriction - object blocked
          console.error("Exception when checking object loaded:", e);
        }
        setObjectLoaded(false);
        return false;
      };

      // Check immediately and after a delay
      const timeout1 = setTimeout(() => checkObjectLoaded(), 1000);
      const timeout2 = setTimeout(() => checkObjectLoaded(), 3000);

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [objectKey]);

  // Expose state: enabled if popup is open OR object tag loaded successfully
  useEffect(() => {
    const isEnabled = (popupWindow !== null && !popupWindow.closed) || objectLoaded;
    onPopupStateChange?.(isEnabled);
  }, [popupWindow, objectLoaded, onPopupStateChange]);

  return (
    <div className="device-config-embed">
      <div className="device-config-embed__actions">
        <MainBtn
          title={t("home:wizard_reload_page")}
          color="secondary"
          onClick={() => {
            setObjectLoaded(false);
            setObjectKey(prev => prev + 1);
          }}
          icon={<ReloadOutlined />}
        />
        <MainBtn
          title="Open in Popup Window"
          color="primary"
          onClick={openDeviceConfigPopup}
          icon={<ExportOutlined />}
        />
      </div>
      <div className="device-config-embed__container">
        <object
        ref={objectRef}
        key={objectKey}
        data={DEVICE_LOCAL_PAGE_URL}
        type="text/html"
        style={{
          width: '100%',
          height: '500px',
          border: 'none',
          display: 'block'
        }}
        onLoad={() => {
          // Object tag loaded - check if content is actually there
          setTimeout(() => {
            try {
              const obj = objectRef.current;
              if (obj) {
                const objDoc = obj.contentDocument || (obj as any).contentWindow?.document;
                if (objDoc && objDoc.body && objDoc.body.innerHTML.trim().length > 0) {
                  const bodyText = objDoc.body.textContent || '';
                  const isErrorPage = bodyText.includes('ERR_') || 
                                    bodyText.includes('blocked') ||
                                    bodyText.includes('refused') ||
                                    bodyText.includes('X-Frame-Options') ||
                                    bodyText.includes('cannot be embedded');
                  
                  if (!isErrorPage) {
                    setObjectLoaded(true);
                  }
                }
              }
            } catch (e) {
              // Cross-origin restriction - object blocked
              console.error("Exception when checking object loaded:", e);
              setObjectLoaded(false);
            }
          }, 500);
        }}
        onError={() => {
          // Object tag failed, will show fallback
          setObjectLoaded(false);
        }}
      >
        <div style={{
          padding: '60px 40px',
          textAlign: 'center',
          backgroundColor: '#fafafa',
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <p style={{ marginBottom: '16px', color: '#666', fontSize: '16px', fontWeight: 500 }}>
            {fallbackTitle || t("home:wizard_configuring_home_wifi")}
          </p>
          <p style={{ color: '#999', fontSize: '14px', marginBottom: '24px' }}>
            {fallbackDescription || "The device configuration page cannot be embedded directly. Please use the buttons above to reload or open in a popup window."}
          </p>
        </div>
      </object>
      </div>
    </div>
  );
};

export default DeviceConfigurationEmbed;

